import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken } from 'firebase/app-check';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/storage';

const FIREBASE_INIT_PARAMS = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Resolves 'ready' once the first App Check token attempt either succeeds or
// times out (5s) - safe to proceed either way, since Firebase's own Auth SDK
// transparently awaits the same underlying fetch on the actual request even
// after this local timeout elapses. Resolves 'failed' only if the fetch
// genuinely rejected - a request made in that state would carry an invalid
// dummy token, so callers should show a distinct state rather than a normal
// sign-in button.
export type AppCheckOutcome = 'ready' | 'failed';
export let appCheckReady: Promise<AppCheckOutcome> = Promise.resolve('ready');

if (Object.values(FIREBASE_INIT_PARAMS).every((v) => !!v)) {
  if (process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN) {
    (window as unknown as Record<string, unknown>).FIREBASE_APPCHECK_DEBUG_TOKEN =
      process.env.REACT_APP_FIREBASE_APPCHECK_DEBUG_TOKEN;
  }
  firebase.initializeApp(FIREBASE_INIT_PARAMS);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (typeof window !== 'undefined' && siteKey) {
    try {
      const appCheck = initializeAppCheck(firebase.app(), {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
      appCheckReady = Promise.race([
        getToken(appCheck).then(
          (): AppCheckOutcome => 'ready',
          (error): AppCheckOutcome => {
            console.error('Firebase App Check first token fetch failed:', error);
            return 'failed';
          },
        ),
        new Promise<AppCheckOutcome>((resolve) => setTimeout(() => resolve('ready'), 5000)),
      ]);
    } catch (error) {
      console.error('Firebase App Check initialization failed:', error);
    }
  }
}

// FirebaseUI (react-firebaseui's StyledFirebaseAuth) creates its own secondary Firebase app
// instance internally (`<app.name>-firebaseui-temp`) and runs signInWithPopup against it, not
// the primary app App Check is initialized on above - so the temp app never gets an App Check
// provider unless this also runs. Call this from StyledFirebaseAuth's uiCallback prop, which
// fires right after the widget (and its temp app) is constructed, before .start() renders the
// sign-in button.
let firebaseUITempAppCheckInitialized = false;
export const initFirebaseUITempAppCheck = (): void => {
  if (firebaseUITempAppCheckInitialized) return;
  firebaseUITempAppCheckInitialized = true;
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  if (typeof window === 'undefined' || !siteKey) return;
  const primaryApp = firebase.app();
  const tempApp = firebase.apps.find((app) => app.name === `${primaryApp.name}-firebaseui-temp`);
  if (!tempApp) return;
  // FirebaseUI creates this app with only apiKey and authDomain. App Check also needs
  // projectId and appId to build its token-exchange endpoint, so inherit the complete
  // primary-app configuration before initializing it.
  if (tempApp.options && primaryApp.options) {
    Object.assign(tempApp.options, primaryApp.options);
  }
  try {
    initializeAppCheck(tempApp, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (error) {
    console.error('Firebase App Check initialization failed (FirebaseUI temp app):', error);
  }
};

type FirebaseUser = firebase.User | null;
export interface UserVerification {
  id: string;
  status?: Number;
  firebaseUser: FirebaseUser;
  supplier_id?: undefined | number;
  role: {
    name: string;
    is_admin: boolean;
    id: string;
    permissions: string[];
  };
}

export const getFirebaseUser = (): FirebaseUser => firebase.auth().currentUser;

export const getFirebaseIdToken = async (): Promise<string> => {
  const firebaseUser = getFirebaseUser();
  return !!firebaseUser ? await firebaseUser.getIdToken() : '';
};

export const firebaseSignOut = async (): Promise<void> => {
  await firebase
    .auth()
    .signOut()
    .then(() => {
      localStorage.removeItem('token');
      window.location.replace('/login');
    });
};

export const storageBucket = 'gs://shopeetw.appspot.com/';
export const storage = firebase.app().storage(storageBucket);
export const storageTaskEvent = firebase.storage.TaskEvent;

export default firebase;
